import React, { useState, useEffect } from 'react';
import { formatEmpIdForDisplay } from '../../utils/empIdUtils';
import { fetchProfileImage, getCachedProfileImage } from '../../utils/profileImageService';

interface EmployeeAvatarProps {
  empName?: string;
  empImage?: string;
  empId?: string;
  size?: 'sm' | 'md' | 'lg';
  shape?: 'circle' | 'square' | 'rounded';
  showName?: boolean;
  showEmpId?: boolean;
  className?: string;
}

const EmployeeAvatar: React.FC<EmployeeAvatarProps> = ({ 
  empName, 
  empImage, 
  empId, 
  size = 'md',
  shape = 'rounded',
  showName = true,
  showEmpId = true,
  className = ''
}) => {
  const [resolvedSrc, setResolvedSrc] = useState<string | null>(() => {
    return getCachedProfileImage(empImage) || getCachedProfileImage(empId) || null;
  });
  const [imageError, setImageError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  
  const displayName = empName && empName !== 'N/A' && empName.trim() ? empName : 'Unknown Employee';
  
  // Handle admin/approver users
  const isAdmin = displayName.toLowerCase().includes('admin') || displayName.toLowerCase().includes('approver');
  
  const initials = displayName !== 'Unknown Employee' 
    ? displayName.split(' ')
        .filter(Boolean)
        .map(n => n && n[0] ? n[0] : '')
        .join('')
        .substring(0, 2)
        .toUpperCase() 
    : 'UE';

  useEffect(() => {
    let isMounted = true;
    setImageError(false);
    setImageLoaded(false);

    const source = empImage || empId;
    if (!source) {
      setResolvedSrc(null);
      return;
    }

    const cached = getCachedProfileImage(source);
    if (cached) {
      setResolvedSrc(cached);
      return;
    }

    fetchProfileImage(source).then((url) => {
      if (isMounted) {
        setResolvedSrc(url);
      }
    });

    return () => {
      isMounted = false;
    };
  }, [empImage, empId]);

  const showInitials = !resolvedSrc || imageError || !imageLoaded;

  // Size configurations
  const sizeConfig = {
    sm: { width: '34px', height: '34px', fontSize: '12px' },
    md: { width: '42px', height: '42px', fontSize: '14px' },
    lg: { width: '50px', height: '50px', fontSize: '16px' }
  };

  const { width, height, fontSize } = sizeConfig[size];
  const borderRadius = shape === 'circle' ? '50%' : shape === 'square' ? '0px' : '6px';

  // Consistent avatar styles
  const avatarStyles: React.CSSProperties = {
    width,
    height,
    minWidth: width,
    minHeight: height,
    borderRadius,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    flexShrink: 0
  };

  const imageStyles: React.CSSProperties = {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    borderRadius
  };

  // Different colors for different user types
  const backgroundColor = isAdmin ? '#6c757d' : '#0d6efd';

  const initialsStyles: React.CSSProperties = {
    ...avatarStyles,
    backgroundColor,
    color: 'white',
    fontSize,
    fontWeight: '600',
    textAlign: 'center',
    lineHeight: '1'
  };

  return (
    <div className={`d-flex align-items-center gap-3 ${className}`}>
      <div style={avatarStyles}>
        {resolvedSrc && !imageError ? (
          <>
            <img 
              src={resolvedSrc} 
              alt={`${displayName}'s profile`}
              style={{
                ...imageStyles,
                display: showInitials ? 'none' : 'block'
              }}
              onError={() => {
                setImageError(true);
              }}
              onLoad={() => {
                setImageLoaded(true);
              }}
            />
            {showInitials && (
              <div style={initialsStyles}>
                {initials}
              </div>
            )}
          </>
        ) : (
          <div style={initialsStyles}>
            {initials}
          </div>
        )}
      </div>
      
      {(showName || showEmpId) && (
        <div className="flex-grow-1">
          {showEmpId && (
            <div className="fw-medium text-primary">
              {formatEmpIdForDisplay(empId) || "-"}
            </div>
          )}
          {showName && (
            <div className="text-muted small">
              {displayName !== 'Unknown Employee' ? displayName : "-"}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default EmployeeAvatar;
